import React from 'react';
import { FiCheck } from 'react-icons/fi';

const MembershipTierCard = ({
  tier,
  monthlyPrice,
  originalMonthlyPrice,
  quarterlyPrice,
  quarterlySavingsPercent,
  totalCredits,
  frequency,
  features,
  color,
  isPopular,
  saleTag,
  onUpgrade,
  isCurrentPlan = false,
  buttonLabel,
  buttonDisabled = false,
  selectedInterval,
}) => {
  const showSale = Boolean(saleTag);
  const showStrikethrough =
    typeof originalMonthlyPrice === 'number' &&
    originalMonthlyPrice > monthlyPrice;
  const isFreeTier = tier === 'Community';
  const showQuarterlyEmphasis = selectedInterval === 'quarterly' && !isFreeTier && quarterlyPrice > 0;
  const computedLabel =
    buttonLabel || (isCurrentPlan ? 'Current Plan' : isFreeTier ? 'Current Plan' : 'Upgrade');
  const computedDisabled = buttonDisabled || isCurrentPlan || isFreeTier;

  return (
    <div
      className="relative flex flex-col bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden transition-all duration-200 hover:shadow-xl hover:-translate-y-1"
      style={{ borderColor: color }}
    >
      {/* Limited sale ribbon */}
      {showSale && (
        <div className="absolute top-3 right-3 z-10">
          <span className="inline-block bg-gradient-to-r from-rose-500 to-amber-500 text-white text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full shadow-md">
            {saleTag}
          </span>
        </div>
      )}

      {/* Popular badge */}
      {isPopular && (
        <div
          className="py-1 px-4 text-center text-white text-sm font-medium"
          style={{ backgroundColor: color }}
        >
          Most Popular
        </div>
      )}

      {/* Tier header */}
      <div
        className="p-6 text-center"
        style={{ backgroundColor: color, color: tier === 'Community' ? '#333' : '#000' }}
      >
        <h3 className="text-xl font-bold mb-2">{tier}</h3>
        <div className="flex flex-col items-center">
          {showStrikethrough && (
            <div className="text-sm text-gray-700 line-through opacity-80">
              was ${originalMonthlyPrice}/mo
            </div>
          )}
          {showQuarterlyEmphasis ? (
            <>
              <div className="text-4xl font-extrabold">
                ${quarterlyPrice}
                <span className="text-base font-medium">/quarter</span>
              </div>
              <div className="mt-2 text-sm">or ${monthlyPrice}/mo billed monthly</div>
              {quarterlySavingsPercent > 0 && (
                <div className="mt-1 inline-block text-xs font-semibold bg-white/60 text-gray-900 px-2 py-0.5 rounded-full">
                  Save {quarterlySavingsPercent}% quarterly
                </div>
              )}
            </>
          ) : (
            <>
              <div className="text-4xl font-extrabold">
                ${monthlyPrice}
                <span className="text-base font-medium">/mo</span>
              </div>
              {tier !== 'Community' && quarterlyPrice > 0 && (
                <>
                  <div className="mt-2 text-sm">or ${quarterlyPrice}/quarter</div>
                  {quarterlySavingsPercent > 0 && (
                    <div className="mt-1 inline-block text-xs font-semibold bg-white/60 text-gray-900 px-2 py-0.5 rounded-full">
                      Save {quarterlySavingsPercent}% quarterly
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Features */}
      <div className="p-6 flex-grow">
        <div className="mb-4">
          <div className="font-medium mb-1">Credits:</div>
          <div className="text-2xl font-bold">
            {totalCredits}{' '}
            <span className="text-sm font-normal text-gray-600">({frequency})</span>
          </div>
        </div>

        <ul className="space-y-3 mb-6">
          {features.map((feature, index) => (
            <li key={index} className="flex items-start">
              <FiCheck className="mr-2 mt-1 text-green-500 flex-shrink-0" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Upgrade button */}
      <div className="p-6 pt-0">
        <button
          onClick={onUpgrade}
          disabled={computedDisabled}
          className={`w-full py-2 px-4 rounded-lg font-semibold transition-colors duration-200 ${
            computedDisabled ? 'opacity-60 cursor-not-allowed' : 'hover:brightness-95'
          }`}
          style={{
            backgroundColor: color,
            color: tier === 'Community' ? '#333' : '#000',
            border: '1px solid',
            borderColor: tier === 'Community' ? '#ccc' : color
          }}
        >
          {computedLabel}
        </button>
      </div>
    </div>
  );
};

export default MembershipTierCard;
